import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Row, Col, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { IoAddOutline, IoChevronDownOutline } from "react-icons/io5";
import GetData from "../../../services/getData";
import { Link } from "react-router-dom";
import SelectModal from "../../../components/layout/selectModal/SelectModal";
import Toast from "../../../components/layout/toast/Toast";
import "../patients.css";
import "./patientDocs.css";

function PatientPlanList() {
  const { t } = useTranslation("patient");
  const navigate = useNavigate();
  const { patientId } = useParams();
  const [plansList, setPlansList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({});
  const [blocked, setBlocked] = useState(false);
  const [templateType, setTemplateType] = useState("common");
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [showTemplateSelectModal, setShowTemplateSelectModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addMenuRef = useRef(null);

  useEffect(() => {
    if (!showAddMenu) return undefined;
    const handleClickOutside = (event) => {
      if (addMenuRef.current && !addMenuRef.current.contains(event.target)) {
        setShowAddMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAddMenu]);

  const loadPlansList = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await GetData({
        module: "patient",
        block: "treatment_plan_list",
        id_patient: patientId,
      });
      setPlansList(data.result || []);

    } catch (err) {
      console.error("Ошибка загрузки сотрудников:", err);
      setError(err.message || t("plan.list.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async (source = templateType) => {
    setLoadingTemplates(true);
    setSelectedTemplateId("");
    try {
      const response = await GetData({
        module: "patient",
        block: "treatment_plan_list",
        id_patient: patientId,
        action: "get_plan_templates",
        template_source: source === "my" ? "my" : "common",
      });

      setAvailableTemplates(response.templates || []);
    } catch (err) {
      console.error("Ошибка загрузки шаблонов плана лечения:", err);
      setAvailableTemplates([]);
      setToast({
        title: t("plan.list.templatesLoadFailed"),
        status: "error",
      });
    } finally {
      setLoadingTemplates(false);
    }
  };

  const openTemplateModal = async () => {
    setShowAddMenu(false);
    setTemplateType("common");
    setShowTemplateSelectModal(true);
    await loadTemplates("common");
  };

  const handleCreateEmptyPlan = async () => {
    setShowAddMenu(false);
    if (blocked) return;
    setBlocked(true);
    try {
      const response = await GetData({
        module: "patient",
        block: "treatment_plan_list",
        id_patient: patientId,
        action: "create_empty_plan",
        name: t("plan.list.defaultPlanName"),
      });

      const createdPlanId =
        response?.result?.id?.value || response?.new_plan_id || null;

      setToast({
        title: response.message || t("plan.list.emptyPlanCreated"),
        status: response.status || "success",
      });

      await loadPlansList();

      if (createdPlanId) {
        navigate(`/patient/${patientId}/plan/${createdPlanId}?edit=1`);
      }
    } catch (err) {
      console.error("Ошибка создания пустого плана лечения:", err);
      setToast({
        title: t("plan.list.createFailed"),
        status: "error",
      });
    } finally {
      setBlocked(false);
    }
  };

  const handleTemplateTypeChange = async (nextType) => {
    if (nextType === templateType || loadingTemplates) return;
    setTemplateType(nextType);
    await loadTemplates(nextType);
  };

    const formatDateForDisplay = (isoDate) => {
        if (!isoDate) return "—";
        const [datePart] = String(isoDate).trim().split(/[ T]/);
        const [year, month, day] = datePart.split("-");
        if (!year || !month || !day) return "—";
        return `${day}.${month}.${year}`;
    };

    useEffect(() => {
        loadPlansList();
    }, [patientId]);

    const handleOpenPlan = (planId, event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (!planId) return;
        navigate(`/patient/${patientId}/plan/${planId}`);
    };

    const handleOpenPrint = (planId, event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (!planId) return;
        // print-other — актуальная форма печати (PatientPlanPrintOtherFormat);
        // старый вид печати остался доступен по /print.
        navigate(`/patient/${patientId}/plan/${planId}/print-other`);
    };

    const handleOpenRecommendations = (plan, event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (!plan?.id) return;
        navigate(`/patient/${patientId}/plan/${plan.id}/recommendations`);
    };

    if (loading) return <div className="loading">{t("plan.loading")}</div>;
    if (error)
        return <div className="error">{t("plan.list.errorPrefix", { message: error })}</div>;
    if (!plansList) return <div className="no-data">{t("plan.dataNotFound")}</div>;

    console.log(plansList);

    return (
        <div className="pd">
        {toast.title ? <Toast title={toast.title} status={toast.status} /> : false}

        {/* Панель сверху: подсказка слева, кнопка «Добавить» справа */}
        <div className="pd-toolbar">
            <div className="pd-toolbar__hint">{t("plan.list.selectPlanHint")}</div>
            <div className="pd-add" ref={addMenuRef}>
                <button
                    type="button"
                    className="pd-add__btn"
                    aria-haspopup="menu"
                    aria-expanded={showAddMenu}
                    onClick={() => setShowAddMenu((prev) => !prev)}
                >
                    <IoAddOutline size={17} aria-hidden="true" />
                    {t("plan.list.addPlan")}
                    <IoChevronDownOutline
                        size={14}
                        aria-hidden="true"
                        className={`pd-add__caret${showAddMenu ? " is-open" : ""}`}
                    />
                </button>
                {showAddMenu && (
                    <div className="pd-add__menu" role="menu">
                        <button
                            type="button"
                            className="pd-add__menu-item"
                            role="menuitem"
                            onClick={openTemplateModal}
                        >
                            {t("plan.list.addFromTemplate")}
                        </button>
                        <button
                            type="button"
                            className="pd-add__menu-item"
                            role="menuitem"
                            onClick={handleCreateEmptyPlan}
                        >
                            {t("plan.list.addEmptyPlan")}
                        </button>
                    </div>
                )}
            </div>
        </div>

        {plansList.length > 0 ? (
            <Row>
                <Col>
                    <div className="table-list">
                        <Table className="main-table-list rounded-table">
                            <thead>
                                <tr>
                                    <th>{t("plan.fields.name")}</th>
                                    <th>{t("plan.list.createdAt")}</th>
                                    <th>{t("plan.list.actions")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {plansList.map((plan) => (
                                    <tr key={plan.id}>
                                        <td>
                                            <Link to={`/patient/${patientId}/plan/${plan.id}`} className="table-item-link">
                                                <div className="table-item-name">{plan.name}</div>
                                            </Link>
                                        </td>
                                        <td>
                                            <Link to={`/patient/${patientId}/plan/${plan.id}`} className="table-item-link status-link">
                                                <div className="table-item-name">{formatDateForDisplay(plan.date_created)}</div>
                                            </Link>
                                        </td>
                                        <td>
                                            <div className="plan-list-actions">
                                                <button
                                                    type="button"
                                                    className="plan-list-action-btn plan-list-action-btn--icon"
                                                    title={t("plan.list.view")}
                                                    aria-label={t("plan.list.viewAria")}
                                                    onClick={(e) => handleOpenPlan(plan.id, e)}
                                                >
                                                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                                                        <path
                                                            d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"
                                                            fill="currentColor"
                                                        />
                                                    </svg>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="plan-list-action-btn plan-list-action-btn--icon"
                                                    title={t("plan.buttons.print")}
                                                    aria-label={t("plan.list.printAria")}
                                                    onClick={(e) => handleOpenPrint(plan.id, e)}
                                                >
                                                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                                                        <path
                                                            d="M19 8H5a3 3 0 0 0-3 3v4h4v4h12v-4h4v-4a3 3 0 0 0-3-3zm-3 9H8v-5h8v5zm3-7a1 1 0 1 1 0 2 1 1 0 0 1 0-2zM18 3H6v4h12V3z"
                                                            fill="currentColor"
                                                        />
                                                    </svg>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="plan-list-action-btn plan-list-action-btn--text"
                                                    title={t("plan.buttons.recommendations")}
                                                    onClick={(e) => handleOpenRecommendations(plan, e)}
                                                >
                                                    {t("plan.buttons.recommendations")}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                <tr className="table-summary">
                                    <td colSpan={3} className="summary-cell">
                                    {t("plan.list.found", { count: plansList.length })}
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                    </div>
                </Col>
            </Row>
        ) : (
            <div className="no-patients">{t("plan.list.empty")}</div>
        )}

        <SelectModal
          show={showTemplateSelectModal}
          onHide={() => {
            setShowTemplateSelectModal(false);
            setSelectedTemplateId("");
            setAvailableTemplates([]);
          }}
          title={t("plan.list.selectTemplate")}
          loading={loadingTemplates}
          options={availableTemplates}
          value={selectedTemplateId}
          onChange={setSelectedTemplateId}
          topContent={
            <div className="template-type-options mb-3">
              <div className="mb-2">
                <label>
                  <input
                    type="radio"
                    name="templateType"
                    value="common"
                    checked={templateType === "common"}
                    onChange={() => handleTemplateTypeChange("common")}
                    disabled={loadingTemplates}
                  />{" "}
                  {t("plan.list.commonTemplates")}
                </label>
              </div>
              <div>
                <label>
                  <input
                    type="radio"
                    name="templateType"
                    value="my"
                    checked={templateType === "my"}
                    onChange={() => handleTemplateTypeChange("my")}
                    disabled={loadingTemplates}
                  />{" "}
                  {t("plan.list.myTemplates")}
                </label>
              </div>
            </div>
          }
          onSelect={async (selectedTemplate) => {
            if (!selectedTemplate || blocked) return;
            setBlocked(true);
            try {
              const response = await GetData({
                module: "patient",
                block: "treatment_plan_list",
                id_patient: patientId,
                action: "create_plan_from_template",
                id_template: selectedTemplate.id,
              });

              const createdPlanId =
                response?.result?.id?.value || response?.new_plan_id || null;

              setShowTemplateSelectModal(false);
              setSelectedTemplateId("");
              setAvailableTemplates([]);
              setToast({
                title: response.message || t("plan.list.createdFromTemplate"),
                status: response.status || "success",
              });

              await loadPlansList();

              if (createdPlanId) {
                navigate(`/patient/${patientId}/plan/${createdPlanId}?edit=1`);
              }
            } catch (err) {
              console.error("Ошибка создания плана лечения по выбранному шаблону:", err);
              setToast({
                title: t("plan.list.createFailed"),
                status: "error",
              });
            } finally {
              setBlocked(false);
            }
          }}
          optionLabel={(item) => item.name}
          optionValue={(item) => String(item.id)}
          confirmButtonText={t("plan.list.createPlan")}
          emptyMessage={t("plan.list.noTemplates")}
          loadingMessage={t("plan.list.templatesLoading")}
        />
        </div>
    );
}
export default PatientPlanList;
